import Papa from 'papaparse';
import ExcelJS from 'exceljs';

import type {
  ImportRow,
  MatchSuggestion,
  MemberImportField,
  ValueTranslation,
} from '../api/members';

// Shared client-side member-import primitives. Kept here (not on the modal)
// because the onboarding wizard reuses the parse-and-apply-layout pipeline
// for its own import step, which has a different chrome but the same data
// flow.

// Canonical fields the user can map a column to. Mirrors backend
// SupportedFields in internal/services/members/columnmatch.go.
export const FIELDS: { value: MemberImportField; required?: boolean }[] = [
  { value: 'first_name', required: true },
  { value: 'last_name', required: true },
  { value: 'email' },
  { value: 'phone' },
  { value: 'street_address' },
  { value: 'postal_code' },
  { value: 'city' },
  { value: 'personal_id' },
  { value: 'team' },
  { value: 'team_role' },
  { value: 'member_roles' },
];

// RawSheet is the wire shape sent to the analyze endpoint. rows is the FULL
// 2D matrix (every row, every cell, no header assumption). The first
// PREVIEW_ROWS of each sheet are forwarded to the AI; the rest stays on the
// client and is consumed by applyLayout once the AI returns its verdict.
//
// Empty cells appear as "" — important so the AI sees the actual column
// count for rows like the Helsingborgs HC banner where only column 0 is
// populated.
export interface RawSheet {
  sheet_name: string;
  rows: string[][];
}

// SheetLayout is the AI's per-sheet verdict. Snake-case to match the wire
// shape from /import/match-columns. Indices are 0-based.
//
// skip_row_pattern is the column-0 prefix that marks interleaved separator
// rows (e.g. "Till målsman för:" in the parent-child guardian export).
// applyLayout drops any data row whose column-0 cell starts with this
// prefix. Empty when the sheet is plain.
export interface SheetLayout {
  sheet_name: string;
  header_row_index: number;
  skip_rows_before_header: number;
  // skip_row_pattern drops a row entirely when its column-0 cell starts with
  // this prefix (decorative separators). Mutually exclusive with
  // guardian_marker_prefix — a row is either a separator to discard, OR a
  // real data row carrying a guardian-of pointer; never both.
  skip_row_pattern: string;
  // guardian_marker_prefix: when set, a row whose column-0 cell starts with
  // this prefix is KEPT as a normal data row, but the trailing subject name
  // (the cell content after the prefix) is preserved as __guardian_of so the
  // backend can link this row's person as that subject's guardian. The
  // column-0 cell value is blanked on the projected row so the marker
  // doesn't leak into any user-mapped column. SportAdmin Se export shape.
  guardian_marker_prefix?: string;
  // guardian_lastname_column_index: when set to 0+, names the column whose
  // bare-last-name cells point at a PRECEDING row in the same sheet whose
  // Efternamn (last_name) matches. The current row's person is then linked
  // as that earlier row's guardian. Same SportAdmin Se export dialect as
  // guardian_marker_prefix but the SHORTER form ("Johansson" alone vs. the
  // full "Till målsman för: <name>" phrasing); both shapes co-exist within
  // a single sheet so the two fields are independent. -1 (or absent) = no
  // lastname-lookback column on this sheet.
  guardian_lastname_column_index?: number;
  notes?: string;
}

// GUARDIAN_OF_KEY is the synthetic per-row key carrying the named subject
// extracted from a guardian-marker cell. Double-underscore prefix matches
// SHEET_KEY's collision-avoidance scheme. Read by transformRows so the
// backend receives it as ImportRow.guardian_of_name.
export const GUARDIAN_OF_KEY = '__guardian_of';

// GUARDIAN_LASTNAME_KEY is the synthetic per-row key carrying a bare last
// name extracted from a guardian-lastname-lookback cell. Resolved inside
// transformRows by walking previously-emitted rows in the same sheet for a
// matching last_name; the matched row's full name then lands on
// ImportRow.guardian_of_name and the backend's phase-2 resolver finishes the
// link by name. Double-underscore prefix matches SHEET_KEY / GUARDIAN_OF_KEY.
export const GUARDIAN_LASTNAME_KEY = '__guardian_lastname';

// ParsedFile — the structured output that the mapping UI + downstream
// transformRows consume. Produced by applyLayout from the (rawSheets,
// layouts) pair.
export interface ParsedFile {
  headers: string[];
  rows: Record<string, string>[];
  // sheetNames is the list of source sheets that contributed rows. CSV
  // imports always have a single anonymous sheet (empty array). XLSX with
  // multiple sheets fills this — and each row in `rows` carries an extra
  // `__sheet` key naming the sheet it came from. Used to drive the
  // sheet-as-team UX: when team isn't explicitly mapped, the sheet name
  // becomes the team.
  sheetNames: string[];
}

// SHEET_KEY is the synthetic per-row key carrying the source sheet name.
// Double-underscore prefix to make collisions with real headers impossible
// (those would have to start with __ to clash, which never happens in
// member rosters).
export const SHEET_KEY = '__sheet';

// PREVIEW_ROWS is how many rows from the top of each sheet we send to the
// AI for layout sniffing. 20 is enough to see the banner + header + a few
// data rows on the messiest exports we've encountered without blowing up
// the token budget (20 rows × ~10 columns × ~3 sheets ≈ 600 cells; well
// under Haiku's input window even with the system prompt).
export const PREVIEW_ROWS = 20;

// FieldMapping: which source column feeds each canonical field. Empty /
// missing = field is not imported. The "AI splits combined names" UX is
// expressed by pointing BOTH first_name AND last_name at the same source
// column — the transformer then splits the cell on the last whitespace.
export type FieldMapping = Partial<Record<MemberImportField, string>>;

// suggestionsToMapping converts the server's column→field shape (one entry
// per source column, with confidence) into our field→column shape (one
// entry per canonical field). When the AI says the same column feeds two
// fields (typical for combined-name columns: first_name and last_name both
// point at "Namn"), both entries are preserved — the transformer detects
// the shared source and splits.
export function suggestionsToMapping(suggestions: MatchSuggestion[]): FieldMapping {
  // Pick the highest-confidence column for each field. With AI, multiple
  // columns rarely target the same field; with fuzzy the duplicate-resolver
  // already dedupes upstream.
  const byField: Record<string, { column: string; confidence: number }> = {};
  for (const s of suggestions) {
    if (!s.field) continue;
    const prev = byField[s.field];
    if (!prev || s.confidence > prev.confidence) {
      byField[s.field] = { column: s.column, confidence: s.confidence };
    }
  }
  const out: FieldMapping = {};
  for (const [field, pick] of Object.entries(byField)) {
    out[field as MemberImportField] = pick.column;
  }
  return out;
}

// extractRawSheets dispatches to CSV (PapaParse) or XLSX (exceljs) based on
// the filename suffix; if neither matches we fall through to CSV which
// handles nearly any text file gracefully.
//
// Output is the FULL 2D matrix for each sheet — no row 0 = header
// assumption, no blank-row skipping. The matrix is later passed to
// applyLayout(rawSheets, layouts) once the AI tells us which row holds the
// headers and which interleaved rows to drop.
export async function extractRawSheets(file: File): Promise<RawSheet[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.tsv') || file.type === 'text/csv') {
    return extractRawSheetsFromCSV(file);
  }
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return extractRawSheetsFromXLSX(file);
  }
  return extractRawSheetsFromCSV(file);
}

function extractRawSheetsFromCSV(file: File): Promise<RawSheet[]> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: false, // banner / blank rows must stay visible to the AI
      dynamicTyping: false,
      complete: (results) => {
        const rows = (results.data ?? []).map((r) =>
          r.map((c) => (c == null ? '' : String(c))),
        );
        // Drop trailing fully-empty rows — PapaParse sometimes emits a
        // single trailing empty row at EOF that adds noise without
        // signal.
        while (rows.length > 0) {
          const last = rows[rows.length - 1]!;
          if (!last.every((c) => c.trim() === '')) break;
          rows.pop();
        }
        resolve([{ sheet_name: '', rows }]);
      },
      error: (err) => reject(err),
    });
  });
}

async function extractRawSheetsFromXLSX(file: File): Promise<RawSheet[]> {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  if (wb.worksheets.length === 0) return [];

  const out: RawSheet[] = [];
  for (const sheet of wb.worksheets) {
    const sheetName = (sheet.name ?? '').trim();
    // columnCount counts only the columns ExcelJS has tracked dimensions
    // for. Use Math.max with each row's actualCellCount as a safety net
    // for files where the dimensions header is wrong (some exporters
    // produce these).
    let colCount = sheet.columnCount;
    for (let i = 1; i <= sheet.rowCount; i++) {
      const r = sheet.getRow(i);
      if (r.actualCellCount > colCount) colCount = r.actualCellCount;
    }
    if (colCount < 1) colCount = 1;

    const rows: string[][] = [];
    for (let i = 1; i <= sheet.rowCount; i++) {
      const row = sheet.getRow(i);
      const cells: string[] = [];
      for (let c = 1; c <= colCount; c++) {
        const v = row.getCell(c).value;
        const s = stringifyCellValue(v);
        cells.push(s.trim());
      }
      rows.push(cells);
    }
    // Same trailing-empty-row guard as CSV.
    while (rows.length > 0) {
      const last = rows[rows.length - 1]!;
      if (!last.every((c) => c === '')) break;
      rows.pop();
    }
    out.push({ sheet_name: sheetName, rows });
  }
  return out;
}

// stringifyCellValue normalizes ExcelJS's cell value union into a string.
// Excel-formula cells expose `{ result, formula }`; rich-text cells expose
// `{ richText: [...] }`; hyperlink cells expose `{ text, hyperlink }`. We
// want the displayed text in every case.
function stringifyCellValue(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    // Hyperlink cell: { text, hyperlink }
    if (typeof o.text === 'string') return o.text;
    // Rich-text cell: { richText: [{ text }, ...] }
    if (Array.isArray(o.richText)) {
      return (o.richText as Array<{ text?: string }>)
        .map((seg) => seg?.text ?? '')
        .join('');
    }
    // Formula cell: { result, formula }
    if ('result' in o) return stringifyCellValue(o.result);
  }
  return String(v);
}

// previewSheets trims each sheet's rows to PREVIEW_ROWS for the AI request.
// The full matrix is retained on the client and consumed by applyLayout
// after the AI returns its verdict.
export function previewSheets(sheets: RawSheet[]): RawSheet[] {
  return sheets.map((s) => ({
    sheet_name: s.sheet_name,
    rows: s.rows.slice(0, PREVIEW_ROWS),
  }));
}

// defaultLayouts produces a "plain" layout per sheet (header row 0, no
// skips). Used as the fallback when the analyze endpoint is unreachable or
// returns no layouts — the import then behaves exactly like the legacy
// "row 0 is the header" path.
export function defaultLayouts(sheets: RawSheet[]): SheetLayout[] {
  return sheets.map((s) => ({
    sheet_name: s.sheet_name,
    header_row_index: 0,
    skip_rows_before_header: 0,
    skip_row_pattern: '',
  }));
}

// applyLayout walks each raw sheet using its layout to produce the keyed
// ParsedFile the rest of the pipeline already understands. Layouts are
// matched to sheets by INDEX (not name) — the analyze endpoint guarantees
// one layout per input sheet, in input order.
//
// Three transformations happen here:
//   1. header_row_index picks the header row (instead of assuming row 0).
//   2. skip_row_pattern drops interleaved label rows (e.g. "Till målsman
//      för:" separators) whose column-0 cell starts with the prefix.
//   3. The cross-sheet header union is rebuilt from each sheet's identified
//      header row (the AI may have identified different header rows on
//      different sheets — multi-sheet workbooks aren't required to be
//      uniform).
//
// Rows with no content after applying the header projection are dropped,
// matching the old parser's behaviour.
export function applyLayout(
  sheets: RawSheet[],
  layouts: SheetLayout[],
): ParsedFile {
  const headerOrder: string[] = [];
  const headerSeen = new Set<string>();
  const allRows: Record<string, string>[] = [];
  const sheetNames: string[] = [];

  sheets.forEach((sheet, i) => {
    const layout =
      layouts[i] ??
      ({
        sheet_name: sheet.sheet_name,
        header_row_index: 0,
        skip_rows_before_header: 0,
        skip_row_pattern: '',
      } satisfies SheetLayout);
    sheetNames.push(sheet.sheet_name);

    const hdrIdx = layout.header_row_index;
    if (hdrIdx < 0 || hdrIdx >= sheet.rows.length) return;

    const headerRow = sheet.rows[hdrIdx]!;
    // Sheet-local list of (column index, header name) pairs. We track
    // column INDEX because trimmed empty headers between real ones would
    // otherwise shift subsequent cells leftward. Real headers (non-empty)
    // contribute to the cross-sheet union in first-seen order.
    const sheetHeaders: { col: number; name: string }[] = [];
    headerRow.forEach((cell, col) => {
      const h = cell.trim();
      if (!h) return;
      sheetHeaders.push({ col, name: h });
      if (!headerSeen.has(h)) {
        headerSeen.add(h);
        headerOrder.push(h);
      }
    });

    const skipPattern = (layout.skip_row_pattern ?? '').trim();
    const guardianPrefix = (layout.guardian_marker_prefix ?? '').trim();
    const guardianLastnameCol =
      typeof layout.guardian_lastname_column_index === 'number' &&
      layout.guardian_lastname_column_index >= 0
        ? layout.guardian_lastname_column_index
        : -1;

    for (let r = hdrIdx + 1; r < sheet.rows.length; r++) {
      const dataRow = sheet.rows[r]!;
      // Interleaved separator-row drop. The AI returns the prefix; we
      // apply it to column 0 of every data row in the full file (not just
      // the rows the AI saw in its 20-row preview).
      if (skipPattern && (dataRow[0] ?? '').startsWith(skipPattern)) continue;

      // Guardian-marker extraction. When column 0 starts with the marker
      // prefix, the rest of that cell names the subject this row's person
      // is a guardian of. Keep the row, stash the subject under
      // GUARDIAN_OF_KEY, and clear column 0 so the marker text isn't
      // mistaken for a user-mapped field value below.
      let guardianOf = '';
      let guardianLastname = '';
      const projectedRow = dataRow.slice();
      if (guardianPrefix) {
        const cell0 = (projectedRow[0] ?? '').trim();
        if (cell0.toLowerCase().startsWith(guardianPrefix.toLowerCase())) {
          guardianOf = cell0.slice(guardianPrefix.length).trim();
          projectedRow[0] = '';
        }
      }
      // Guardian-lastname extraction. When the indicator column carries a
      // non-empty cell that did NOT match the full prefix above, treat it
      // as a bare last-name pointer. Same column may host both shapes
      // ("Till målsman för: <full name>" on some rows, "Johansson" alone on
      // others), so we only fall into this branch when the prefix branch
      // didn't claim the cell.
      if (guardianLastnameCol >= 0 && !guardianOf) {
        const cell = (projectedRow[guardianLastnameCol] ?? '').trim();
        if (cell) {
          guardianLastname = cell;
          projectedRow[guardianLastnameCol] = '';
        }
      }

      const obj: Record<string, string> = {};
      let hasContent = false;
      for (const { col, name } of sheetHeaders) {
        const v = (projectedRow[col] ?? '').trim();
        obj[name] = v;
        if (v) hasContent = true;
      }
      // A pointer row with a non-empty extracted subject counts as content
      // even if every mapped column is blank — Sigrid-style rows where the
      // person only appears as a guardian still need to flow through.
      if (hasContent || guardianOf || guardianLastname) {
        obj[SHEET_KEY] = sheet.sheet_name;
        if (guardianOf) obj[GUARDIAN_OF_KEY] = guardianOf;
        if (guardianLastname) obj[GUARDIAN_LASTNAME_KEY] = guardianLastname;
        allRows.push(obj);
      }
    }
  });

  return { headers: headerOrder, rows: allRows, sheetNames };
}

// uniqueRoleValuesFromRows returns the distinct non-empty cell values found
// in whichever source columns the user mapped to team_role and member_roles.
// Used to drive the AI value-translation step before submission: the server
// classifies each unique value once and the frontend caches the result for
// the whole batch.
export function uniqueRoleValuesFromRows(
  rows: Record<string, string>[],
  mapping: FieldMapping,
): string[] {
  const cols: string[] = [];
  if (mapping.team_role) cols.push(mapping.team_role);
  if (mapping.member_roles) cols.push(mapping.member_roles);
  if (cols.length === 0) return [];
  const seen = new Set<string>();
  for (const r of rows) {
    for (const c of cols) {
      const raw = (r[c] ?? '').trim();
      if (!raw) continue;
      // member_roles cells may pack multiple values: "member,board".
      for (const piece of raw.split(/[,;|]/)) {
        const v = piece.trim();
        if (v && !seen.has(v.toLowerCase())) seen.add(v.toLowerCase());
      }
    }
  }
  return Array.from(seen);
}

// transformRows projects raw header→value rows into the canonical ImportRow
// shape using the user-confirmed field→column mapping.
//
// Combined-name columns: when first_name and last_name reference the same
// source column, the cell is split on the LAST whitespace. "Anna Maria
// Andersson" → first_name="Anna Maria", last_name="Andersson". This matches
// Swedish/Nordic convention where given names can be multiple but the
// family name is usually a single token.
//
// member_roles: a single cell can hold "member,board" — split on comma /
// semicolon / pipe and lowercase each. Empty → field omitted.
//
// Value translations: optional. When provided, raw role values like
// "Förälder" get rewritten to canonical codes ("guardian"), and values
// whose AI-classified axis differs from the column they live in (e.g.
// "Förälder" in a column mapped to team_role) get routed to the OTHER
// axis. This is the same routing the backend's normalizeImportRow does —
// putting it here too means the JSON sent over the wire is already
// canonical and the per-row validation errors point at the right cells.
export function transformRows(
  rows: Record<string, string>[],
  mapping: FieldMapping,
  translations: ValueTranslation[] = [],
): ImportRow[] {
  // Build a case-insensitive lookup from the translations array.
  const trans = new Map<string, ValueTranslation>();
  for (const t of translations) {
    if (t.code && t.axis) trans.set(t.value.toLowerCase(), t);
  }
  const lookup = (raw: string): ValueTranslation | undefined => trans.get(raw.toLowerCase());

  const firstCol = mapping.first_name;
  const lastCol = mapping.last_name;
  const splitCombined = !!firstCol && firstCol === lastCol;

  // Per-sheet running list of previously-emitted rows, in order, for the
  // bare-last-name guardian lookback. Each sheet is independent (same-sheet
  // matching only, per the SportAdmin Se export shape — rows above in the
  // same sheet share a household; cross-sheet matches would be surprising
  // and complicate idempotency on re-import).
  const emittedBySheet = new Map<string, ImportRow[]>();

  const out: ImportRow[] = [];
  for (const row of rows) {
    const get = (col: string | undefined) => (col ? (row[col] ?? '').trim() : '');

    let firstName = '';
    let lastName = '';
    if (splitCombined && firstCol) {
      const combined = get(firstCol);
      const idx = combined.lastIndexOf(' ');
      if (idx > 0) {
        firstName = combined.slice(0, idx).trim();
        lastName = combined.slice(idx + 1).trim();
      } else {
        // Single token in a "full name" cell — treat as last_name only.
        // Nordic clubs often have founders / honorary members listed by
        // surname only; better to surface that than to silently mangle.
        lastName = combined;
      }
    } else {
      firstName = get(firstCol);
      lastName = get(lastCol);
    }

    const emitted: ImportRow = {
      first_name: firstName,
      last_name: lastName,
    };
    const email = get(mapping.email);
    if (email) emitted.email = email;
    const city = get(mapping.city);
    if (city) emitted.city = city;
    // Team: explicit mapping wins. Otherwise, if the row carries a __sheet
    // tag (multi-sheet XLSX), fall back to the sheet name. Single-sheet
    // CSV/XLSX with no team mapping → no team assignment.
    const phone = get(mapping.phone);
    if (phone) emitted.phone = phone;
    const streetAddress = get(mapping.street_address);
    if (streetAddress) emitted.street_address = streetAddress;
    const postalCode = get(mapping.postal_code);
    if (postalCode) emitted.postal_code = postalCode;
    // National ID is sent verbatim — server runs the country validator and
    // either canonicalises it (status='valid') or stores raw with
    // status='needs_review'. No client-side preprocessing here.
    const personalId = get(mapping.personal_id);
    if (personalId) emitted.personal_id = personalId;
    // Team mapping has three explicit modes (no silent fallback):
    //   - mapping.team is a real header     → use that cell's value per row
    //   - mapping.team === SHEET_KEY        → use the sheet name (multi-sheet
    //                                         XLSX where each tab is a team)
    //   - mapping.team is empty / undefined → no team assignment
    // Earlier versions silently fell back to the sheet name when team was
    // unmapped; the UX hid this and made "(Ignore)" mean two different things.
    const team =
      mapping.team === SHEET_KEY
        ? (row[SHEET_KEY] ?? '').trim()
        : get(mapping.team);
    if (team) emitted.team = team;

    // Role values: apply translations + axis routing. Pre-routing the values
    // means the backend sees clean canonical codes; the static map there
    // remains as a safety net for direct API consumers.
    const memberRolesAccum: string[] = [];
    let teamRoleResolved = '';

    const rawTeamRole = get(mapping.team_role);
    if (rawTeamRole) {
      const tr = lookup(rawTeamRole);
      if (tr && tr.axis === 'member_role') {
        memberRolesAccum.push(tr.code);
      } else if (tr && tr.axis === 'team_role') {
        teamRoleResolved = tr.code;
      } else {
        teamRoleResolved = rawTeamRole; // pass through; backend has its own map
      }
    }

    const rawMemberRoles = get(mapping.member_roles);
    if (rawMemberRoles) {
      const pieces = rawMemberRoles.split(/[,;|]/).map((s) => s.trim()).filter(Boolean);
      for (const piece of pieces) {
        const tr = lookup(piece);
        if (tr && tr.axis === 'team_role') {
          if (!teamRoleResolved) teamRoleResolved = tr.code;
          continue;
        }
        memberRolesAccum.push(tr && tr.axis === 'member_role' ? tr.code : piece.toLowerCase());
      }
    }

    if (teamRoleResolved) emitted.team_role = teamRoleResolved;
    if (memberRolesAccum.length > 0) {
      // Dedupe.
      emitted.member_roles = Array.from(new Set(memberRolesAccum));
    }
    // Guardian-of pointer (SportAdmin Se "Till målsman för: <name>" pattern).
    // Stashed into the row by applyLayout when the sheet's layout carried
    // guardian_marker_prefix; passed through verbatim so the backend's
    // phase-2 resolver can look up the subject by name.
    const guardianOf = (row[GUARDIAN_OF_KEY] ?? '').trim();
    if (guardianOf) emitted.guardian_of_name = guardianOf;

    // Bare-last-name pointer (SportAdmin Se shorter variant: column A holds
    // just "Johansson" / "Abrahamsson"). Resolve here by walking the rows we
    // already emitted from the SAME sheet, from most-recent backward, for
    // the first whose last_name matches case-insensitively. Skip rows that
    // are themselves guardian-pointer rows — a row that's still a child for
    // matching purposes hasn't yet been claimed by another guardian, but a
    // row that already pointed elsewhere is a guardian and shouldn't double
    // as a child for a later sibling row. When we find a match, set
    // guardian_of_name to "<first> <last>" so the backend's phase-2 resolver
    // links by name like any other guardian pointer. No match → leave
    // unresolved; the backend will emit a "guardian_subject_not_found" skip
    // notice the user can review post-import.
    //
    // The explicit-name pointer (GUARDIAN_OF_KEY → guardian_of_name above)
    // wins when both shapes somehow land on the same row.
    const sheetName = (row[SHEET_KEY] ?? '').trim();
    if (!emitted.guardian_of_name) {
      const lastnameMarker = (row[GUARDIAN_LASTNAME_KEY] ?? '').trim().toLowerCase();
      if (lastnameMarker) {
        const sheetEmitted = emittedBySheet.get(sheetName) ?? [];
        for (let i = sheetEmitted.length - 1; i >= 0; i--) {
          const candidate = sheetEmitted[i]!;
          if (candidate.guardian_of_name) continue;
          if ((candidate.last_name ?? '').trim().toLowerCase() === lastnameMarker) {
            const fullName = `${candidate.first_name} ${candidate.last_name}`.trim();
            if (fullName) emitted.guardian_of_name = fullName;
            break;
          }
        }
      }
    }

    out.push(emitted);
    const sheetList = emittedBySheet.get(sheetName) ?? [];
    sheetList.push(emitted);
    emittedBySheet.set(sheetName, sheetList);
  }
  return out;
}

// canImport — both first_name and last_name must be mapped to some source
// column. They MAY share a column (combined-name split path).
export function canImport(mapping: FieldMapping): boolean {
  return !!mapping.first_name && !!mapping.last_name;
}

// applySheetTeamDefault opts the team mapping in to "use sheet names" when
// the file has multiple sheets AND no team column was already mapped
// (either by the AI/fuzzy matcher or explicitly by the user). Called after
// suggestionsToMapping so it never overwrites a real column choice.
//
// Why this exists: previously transformRows silently fell back to sheet
// names when team was unmapped, so the UI dropdown showed "(Ignore)" while
// teams were being created behind the scenes. Now the SHEET_KEY value is
// surfaced as an explicit "Use sheet names" option in the dropdown — this
// helper just makes it the default.
export function applySheetTeamDefault(
  mapping: FieldMapping,
  sheetNames: string[],
): FieldMapping {
  if (sheetNames.length <= 1) return mapping;
  if (mapping.team) return mapping;
  return { ...mapping, team: SHEET_KEY };
}
